import axios from "axios";

// Defensive: strip trailing slash so we never produce `https://host//api/...`,
// which on this platform's ingress matches the SPA catch-all and returns HTML
// instead of JSON — crashing every `.map()` downstream.
const RAW = process.env.REACT_APP_BACKEND_URL || "";
const BACKEND_URL = RAW.replace(/\/+$/, "");
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// ── Iter-196.KK Sprint 3 — CSRF token handling ──────────────────
// On every mutating request (POST/PUT/PATCH/DELETE) we attach the
// `X-CSRF-Token` header. Backend rejects cookie-authenticated
// mutations without it. Token is sourced from /api/auth/me and
// cached in a module-level ref; refreshed when the response carries
// an updated token (handled via the response interceptor below).
let _csrfToken = null;

export const setCsrfToken = (token) => {
  _csrfToken = token || null;
};

export const getCsrfToken = () => _csrfToken;

api.interceptors.request.use((config) => {
  const method = String(config.method || "get").toUpperCase();
  if (["POST", "PUT", "PATCH", "DELETE"].includes(method) && _csrfToken) {
    config.headers = config.headers || {};
    config.headers["X-CSRF-Token"] = _csrfToken;
  }
  return config;
});

// Reject HTML responses (proxy/edge fallthrough) so the SPA never tries to
// `.map()` over an HTML document. Throw so React Query / .catch handlers fire.
api.interceptors.response.use((response) => {
  const ct = String(response.headers?.["content-type"] || "");
  if (ct.includes("text/html")) {
    throw new Error(
      `API returned HTML instead of JSON for ${response.config?.url}. ` +
        "Check REACT_APP_BACKEND_URL for a trailing slash or wrong host."
    );
  }
  return response;
});

// Array-shape helper: every endpoint we call as a list returns an array.
// If something upstream breaks and returns null/object/string, we coerce
// to [] so the UI degrades gracefully instead of unmounting.
const asArray = (v) => (Array.isArray(v) ? v : []);

export const submitQuote = (payload) => api.post("/quote-requests", payload).then((r) => r.data);
export const subscribeNewsletter = (email, opts = {}) =>
  api
    .post("/newsletter", {
      email,
      // Iter-141 — capture the SPA pathname at signup time so the backend
      // can enqueue the cluster-tailored 5-email nurture drip.
      source_path:
        opts.source_path ||
        (typeof window !== "undefined" ? window.location.pathname : undefined),
      source_cluster: opts.source_cluster || undefined,
      // Iter-196.X (F-MON-04 from audit) — Vertical-interest array
      // captured from the new 4-checkbox row above the email input.
      // Backend persists `interests[]` on the subscriber doc so
      // future Resend audience routing can segment drips by
      // category cohort (Insurance / Loans / Warranty / Refinance).
      // No-op when caller doesn't pass an array — preserves legacy
      // signup paths and Quote-modal-triggered signups.
      interests:
        Array.isArray(opts.interests) && opts.interests.length
          ? opts.interests
          : undefined,
      // Iter-196.WW (L-6) — Honeypot field forwarded as-is. Real
      // users never type into the hidden input so this is "" for
      // legitimate signups; bots fill every field.
      website: opts.website || undefined,
    })
    .then((r) => r.data);

// Iter-275.AZ (Phase 6 #25) — Newsletter DOI confirmation. Reads
// the magic-link token from the URL on the `/newsletter/confirmed`
// page and hands it to the backend, which moves the row from
// `newsletter_pending_confirmations` → `newsletter` + fires the
// Welcome Series drip. Returns `{ ok, status, email?, message? }`.
export const confirmNewsletter = (token) =>
  api
    .get(`/newsletter/confirm`, { params: { token } })
    .then((r) => r.data);
export const fetchArticles = () => api.get("/articles?limit=500").then((r) => asArray(r.data));
// Iter-275.AT (Phase 2 #4 of Production Readiness Audit) — Lite-mode
// article list for callers that only need card metadata (title, slug,
// hero image, category, cluster) and never read the heavy `body` /
// `quick_answer` / `rendered_body` / `body_excerpt` fields. Drops the
// homepage `ContentHub` payload from ~1 MB → ~80 KB; LCP 9.3s → ≤4s.
export const fetchArticlesLite = ({ limit = 36, category, cluster, tag } = {}) => {
  const params = new URLSearchParams({ lite: "true", limit: String(limit) });
  if (category) params.set("category", category);
  if (cluster) params.set("cluster", cluster);
  if (tag) params.set("tag", tag);
  return api.get(`/articles?${params.toString()}`).then((r) => asArray(r.data));
};
export const fetchArticle = (slug) => api.get(`/articles/${slug}`).then((r) => r.data);
// Iter-185.AU — Article feedback microsurvey.
export const submitArticleFeedback = (slug, payload) =>
  api.post(`/articles/${slug}/feedback`, payload).then((r) => r.data);
export const fetchArticleFeedbackStats = (slug) =>
  api.get(`/articles/${slug}/feedback-stats`).then((r) => r.data);
export const adminListArticleFeedback = (params = {}) =>
  api.get("/admin/article-feedback", { params }).then((r) => r.data);
// Iter-135 — public cluster manifest for the related-guide recommender.
export const fetchClustersManifest = () =>
  api.get("/clusters/manifest").then((r) => r.data);
export const fetchStats = () => api.get("/stats").then((r) => r.data);
export const fetchGeoZip = () => api.get("/geo/zip").then((r) => r.data);
export const fetchLenderRates = (category) =>
  api
    .get("/lender-rates", { params: category ? { category } : {} })
    .then((r) => asArray(r.data));

// ════════════════════════════════════════════════════════════════════
// Phase 6 — Vertical-specific endpoint dispatch (post-soak, flag removed)
// ════════════════════════════════════════════════════════════════════
//
// All 4 vertical-named rate tables fetch from their physically-
// segregated collections via dedicated endpoints. The Phase 5 soak
// window verified zero drift via the parity guardian (3,854 pytests
// + the admin banner), and the transitional env-var feature flag was
// retired in iter-196.H.
//
// The legacy `fetchLenderRates(category)` path remains exported for
// admin-only consumers (the Admin Rates table writes via the legacy
// CRUD chain) and as a defensive fallback for any future category
// that doesn't yet have a vertical-specific endpoint.
//
// Row shape returned by either path is identical (same `LenderRate`
// schema) — the frontend tables consume both transparently.

const VERTICAL_ENDPOINT_BY_CATEGORY = {
  insurance: "/insurance/carriers",
  marketplace: "/insurance/carriers",
  "auto-loan": "/auto-loans/lenders",
  "auto-loan-new": "/auto-loans/lenders",
  "auto-loan-used": "/auto-loans/lenders",
  refinance: "/refinance/lenders",
  warranty: "/warranty/providers",
};

/**
 * Phase 6 — Public read dispatcher for vertical rate tables.
 *
 * Always routes the request to the vertical-specific endpoint when the
 * category maps to one. Falls back to the legacy `/lender-rates?category=X`
 * endpoint defensively for any category that isn't yet in the
 * `VERTICAL_ENDPOINT_BY_CATEGORY` map.
 *
 * Returns the same `LenderRate[]` shape as `fetchLenderRates(category)`.
 */
export const fetchVerticalProviders = (category) => {
  if (VERTICAL_ENDPOINT_BY_CATEGORY[category]) {
    return api
      .get(VERTICAL_ENDPOINT_BY_CATEGORY[category])
      .then((r) => asArray(r.data));
  }
  return fetchLenderRates(category);
};

// Auth
export const exchangeSession = (session_id) =>
  api.post("/auth/session", { session_id }).then((r) => {
    // Iter-196.KK Sprint 3 — capture the CSRF token minted server-side
    // so subsequent mutating admin calls carry the X-CSRF-Token header.
    if (r.data && r.data.csrf_token) setCsrfToken(r.data.csrf_token);
    return r.data;
  });
export const fetchMe = () =>
  api.get("/auth/me").then((r) => {
    if (r.data && r.data.csrf_token) setCsrfToken(r.data.csrf_token);
    return r.data;
  });
export const logout = () => api.post("/auth/logout").then((r) => {
  setCsrfToken(null);
  return r.data;
});

// Admin
export const adminListArticles = () => api.get("/admin/articles").then((r) => r.data);
export const adminCreateArticle = (payload) =>
  api.post("/admin/articles", payload).then((r) => r.data);
export const adminUpdateArticle = (slug, payload) =>
  api.put(`/admin/articles/${slug}`, payload).then((r) => r.data);
export const adminMarkArticleReviewed = (slug) =>
  api.post(`/admin/articles/${slug}/mark-reviewed`).then((r) => r.data);
export const adminDeleteArticle = (slug) =>
  api.delete(`/admin/articles/${slug}`).then((r) => r.data);
export const adminListNewsletter = () => api.get("/admin/newsletter").then((r) => r.data);
export const adminListQuotes = () => api.get("/admin/quotes").then((r) => r.data);
export const adminListContact = () => api.get("/admin/contact").then((r) => r.data);
// Iter-97 — Public exports so a sibling panel (NewsletterBroadcastPanel
// below) can reuse them as needed. Keeping the original API intact.
export const adminListClicks = () => api.get("/admin/clicks").then((r) => r.data);
// Iter-185.J — Pexels stock-photo proxy (admin only). Returns
// normalized photos with photographer attribution preserved.
export const adminPexelsSearch = (params = {}) =>
  api.get("/admin/pexels/search", { params }).then((r) => r.data);
// Iter-91 — Routing webhook playback.
export const adminListPartnerRedirects = (params = {}) =>
  api.get("/admin/partner-redirects", { params }).then((r) => r.data);
// Iter-122 — Affiliate Funnel summary (per-category partner redirects,
// top fallback URLs, email opt-in rate). One-call rollup for the
// /admin/funnel rail.
export const adminFunnelSummary = (params = {}) =>
  api.get("/admin/funnel/summary", { params }).then((r) => r.data);
// Iter-127 — Admin-editable state data + SEO overrides + Web Vitals.
export const fetchStateData = (vertical) =>
  api.get(`/state-data/${vertical}`).then((r) => asArray(r.data));
export const adminListStateData = (params = {}) =>
  api.get("/admin/state-data", { params }).then((r) => asArray(r.data));
export const adminUpsertStateData = (vertical, code, payload) =>
  api.put(`/admin/state-data/${vertical}/${code}`, payload).then((r) => r.data);
export const fetchSeoOverrides = () =>
  api.get("/seo-overrides").then((r) => asArray(r.data));
export const adminListSeoOverrides = () =>
  api.get("/admin/seo-overrides").then((r) => asArray(r.data));
export const adminUpsertSeoOverride = (payload) =>
  api.put("/admin/seo-overrides", payload).then((r) => r.data);
export const adminDeleteSeoOverride = (path) =>
  api.delete("/admin/seo-overrides", { params: { path } }).then((r) => r.data);
export const adminVitalsSummary = (params = {}) =>
  api.get("/admin/vitals/summary", { params }).then((r) => r.data);
// Iter-128 — Topical Authority cluster coverage dashboard.
export const adminTopicalCoverage = () =>
  api.get("/admin/topical-coverage").then((r) => r.data);

// Iter-181-D — Internal-link opportunity scanner. Surfaces every
// canonical CarSavr entity (calculators, pillar guides, hubs, methodology
// pages) that is mentioned in a published article body but not yet linked.
export const adminInternalLinkOpportunities = (params = {}) =>
  api.get("/admin/internal-link-opportunities", { params }).then((r) => r.data);

// Iter-183 — Scheduled internal-link scan snapshot (read-only,
// auto-refreshed every 24h by the backend background task).
export const adminInternalLinkScanSnapshot = () =>
  api.get("/admin/internal-link-scan-snapshot").then((r) => r.data);

// Iter-183 — Calculator defaults registry. Public GET returns the
// active config for every calculator; admin PUT updates one calculator.
export const getCalculatorDefaults = () =>
  api.get("/calculator-defaults").then((r) => r.data);

export const adminUpdateCalculatorDefaults = (calculator, fields) =>
  api
    .put(`/admin/calculator-defaults/${calculator}`, { fields })
    .then((r) => r.data);

// Iter-184 — State-multiplier-adjusted insurance premium lookup.
// Returns either {state_multipliers, carriers} when called without a
// state arg, or {state, state_multiplier, carriers} when filtered.
export const getInsurancePremiumByState = (state) => {
  const params = state ? { state } : {};
  return api.get("/insurance-premium-by-state", { params }).then((r) => r.data);
};
// Iter-97 — Newsletter broadcast composer + history.
export const adminSendNewsletterBroadcast = (payload) =>
  api.post("/admin/newsletter/send-broadcast", payload).then((r) => r.data);
export const adminListNewsletterBroadcasts = (params = {}) =>
  api.get("/admin/newsletter/broadcasts", { params }).then((r) => r.data);
export const adminSummary = () => api.get("/admin/summary").then((r) => r.data);
// Iter-228 — System Health endpoint powering the TODAY dashboard's
// health-LED strip. Cheap aggregator (mongo ping + env-var checks).
export const adminSystemHealth = () => api.get("/admin/system-health").then((r) => r.data);
// Iter-275 — TODAY V2 dashboard. "Money this week" tile + activity feed.
// Both are thin aggregators that read existing collections; no new
// data model. See `/app/memory/ADMIN_AUDIT_REPORT.md` §3.3 for the
// dashboard spec these power.
export const adminTodayRevenueWeek = () =>
  api.get("/admin/today/revenue-week").then((r) => r.data);
export const adminTodayActivity = (params = {}) =>
  api.get("/admin/today/activity", { params }).then((r) => r.data);
// Iter-275.B — Email Overview consolidator. Single-screen rollup:
// 7-day KPIs + signup→quote funnel + inbox-health traffic light.
// Spec: /app/memory/EMAIL_SYSTEM_AUDIT.md §4.
export const adminEmailOverview = (params = {}) =>
  api.get("/admin/email/overview", { params }).then((r) => r.data);
// Iter-275.C — Earn (Money) consolidated panel — partner detail drawer.
// One read-only call powers the side-drawer that opens on row click.
// Spec: /app/memory/AFFILIATE_SYSTEM_AUDIT.md §3.
export const adminEarnPartnerDetail = (slug) =>
  api.get(`/admin/earn/partner/${encodeURIComponent(slug)}`).then((r) => r.data);
// Iter-275.E — Founder-editable email copy (P1 #7) + Broadcast composer (P1 #8).
// Spec: /app/memory/EMAIL_SYSTEM_AUDIT.md §2.2 + §2.6.
export const adminEmailCopyList = () =>
  api.get("/admin/email/copy").then((r) => r.data);
export const adminEmailCopySet = (key, value) =>
  api.put(`/admin/email/copy/${encodeURIComponent(key)}`, { value }).then((r) => r.data);
export const adminEmailCopyRevert = (key) =>
  api.delete(`/admin/email/copy/${encodeURIComponent(key)}`).then((r) => r.data);
export const adminBroadcastSegments = () =>
  api.get("/admin/email/broadcast/segments").then((r) => r.data);
export const adminBroadcastSendTest = (payload) =>
  api.post("/admin/email/broadcast/send-test", payload).then((r) => r.data);
export const adminBroadcastSend = (payload) =>
  api.post("/admin/email/broadcast/send", payload).then((r) => r.data);
export const adminBroadcastHistory = (params = {}) =>
  api.get("/admin/email/broadcast/history", { params }).then((r) => r.data);
// Iter-228 — Audit log viewer panel. Backend endpoint exists since
// iter-185.AU; surfacing it as a renderable list for the new System
// super-tab.
export const adminAuditLog = (params = {}) =>
  api.get("/admin/audit-log", { params }).then((r) => r.data);
// Iter-229 — Bulk SEO override CSV + Orphan detector + Affiliate
// health surface (Admin Roadmap Sprint 2 §1.3, §1.6, §1.8).
export const adminSeoOverridesExportUrl = () =>
  `${API}/admin/seo-overrides/export.csv`;
export const adminSeoOverridesBulkUpload = (file, { dry_run = false } = {}) => {
  const form = new FormData();
  form.append("file", file);
  return api
    .post(`/admin/seo-overrides/bulk?dry_run=${dry_run ? "true" : "false"}`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((r) => r.data);
};
export const adminSeoOrphans = (params = {}) =>
  api.get("/admin/seo/orphans", { params }).then((r) => r.data);
// Iter-230 — Orphan auto-suggest engine. Given an orphan slug, returns
// up to N source articles that could plausibly link to it (ranked by
// category match + title-token overlap + inbound link equity).
export const adminSeoOrphanSuggest = (slug, limit = 3) =>
  api.get("/admin/seo/orphans/suggest", { params: { slug, limit } }).then((r) => r.data);
// Iter-262 — One-click bulk link recovery. Bumps every published
// article to >= `floor` editor-written inbound links. Used by the
// "Recover all" button in the Orphan Pages panel to fix production
// in one click (the iter-261/261.B scripts only run against preview).
export const adminSeoOrphanRecover = (floor = 3, maxNewLinksPerSource = 5) =>
  api
    .post("/admin/seo/orphans/recover", null, {
      params: { floor, max_new_links_per_source: maxNewLinksPerSource },
    })
    .then((r) => r.data);

// Iter-231 — Sprint-3 batch (Admin Roadmap).
// RBAC
export const adminMyRole = () => api.get("/admin/me/role").then((r) => r.data);
export const adminListAdmins = () => api.get("/admin/users").then((r) => r.data);
export const adminSetUserRole = (email, role) =>
  api.put(`/admin/users/${encodeURIComponent(email)}/role`, null, { params: { role } }).then((r) => r.data);

// JS error capture
export const reportClientError = (payload) =>
  api.post("/client-errors", payload).then((r) => r.data).catch(() => null);
export const adminListJsErrors = (params = {}) =>
  api.get("/admin/health/js-errors", { params }).then((r) => r.data);

// Partner verifications registry
export const adminListPartnerVerifications = () =>
  api.get("/admin/partner-verifications").then((r) => r.data);
export const adminCreatePartnerVerification = (payload) =>
  api.post("/admin/partner-verifications", payload).then((r) => r.data);
export const adminDeletePartnerVerification = (id) =>
  api.delete(`/admin/partner-verifications/${id}`).then((r) => r.data);

// Affiliate Command Center
export const adminAffiliatePartnersSummary = () =>
  api.get("/admin/affiliate-partners/summary").then((r) => r.data);

// Iter-233 — Trash (soft delete) + Health Center
export const adminListTrash = (params = {}) =>
  api.get("/admin/trash", { params }).then((r) => r.data);
export const adminRestoreTrash = (trashId) =>
  api.post(`/admin/trash/${trashId}/restore`).then((r) => r.data);
export const adminPurgeTrash = (trashId) =>
  api.delete(`/admin/trash/${trashId}/purge`).then((r) => r.data);
export const adminHealthCenter = () =>
  api.get("/admin/health-center").then((r) => r.data);

// Iter-240 — AI Quick Answer + internal-link graph
export const adminGenerateQuickAnswer = (slug) =>
  api.post(`/admin/articles/${encodeURIComponent(slug)}/generate-quick-answer`)
    .then((r) => r.data);
export const adminArticleInternalLinks = (slug, suggestLimit = 12) =>
  api.get(
    `/admin/articles/${encodeURIComponent(slug)}/internal-links`,
    { params: { suggest_limit: suggestLimit } },
  ).then((r) => r.data);
export const adminAffiliateHealth = () =>
  api.get("/admin/lender-rates/affiliate-health").then((r) => r.data);
export const adminRunAffiliateHealthScan = () =>
  api.post("/admin/lender-rates/affiliate-health/scan").then((r) => r.data);
export const adminTestAffiliateLink = (slug) =>
  api.post(`/admin/lender-rates/affiliate-health/${encodeURIComponent(slug)}/test`).then((r) => r.data);
export const adminOverrideAffiliateLink = (slug, working) =>
  api.post(`/admin/lender-rates/affiliate-health/${encodeURIComponent(slug)}/override`, { working }).then((r) => r.data);
// Iter-262.C — Partner rate CRUD. Renamed from `*LenderRate*` to
// `*PartnerRate*` to align with the multi-vertical naming (insurance,
// loans, warranty, refinance). Backend route path `/admin/lender-rates`
// is preserved as a stable URL contract.
export const adminListPartnerRates = () => api.get("/admin/lender-rates").then((r) => asArray(r.data));
export const adminCreatePartnerRate = (payload) =>
  api.post("/admin/lender-rates", payload).then((r) => r.data);
export const adminUpdatePartnerRate = (slug, payload) =>
  api.put(`/admin/lender-rates/${slug}`, payload).then((r) => r.data);
export const adminDeletePartnerRate = (slug) =>
  api.delete(`/admin/lender-rates/${slug}`).then((r) => r.data);
export const adminTouchAllPartnerRates = () =>
  api.post("/admin/lender-rates/touch-all").then((r) => r.data);
// Iter-413 — One-click editorial review stamp for articles.
// scope: "stale" (default, only 30d+ old stamps) or "all".
export const adminMarkArticlesReviewed = (scope = "stale") =>
  api.post(`/admin/articles/mark-reviewed?scope=${scope}`).then((r) => r.data);
export const adminAuditPartnerRates = () =>
  api.get("/admin/lender-rates/audit").then((r) => r.data);

// Iter-196.BB — City editorial LLM generator + draft review.
// Powered by Claude Sonnet 4.5 via the universal Emergent LLM key.
// Drafts persist in `city_editorial_drafts`; approved drafts are
// exported as a JS object-literal patch for cityEditorial.js.
export const adminCityEditorialGenerate = (payload) =>
  api.post("/admin/city-editorial/generate", payload).then((r) => r.data);
export const adminCityEditorialListDrafts = (status) => {
  const params = status ? { status } : {};
  return api.get("/admin/city-editorial/drafts", { params }).then((r) => r.data);
};
export const adminCityEditorialGetDraft = (slug) =>
  api.get(`/admin/city-editorial/drafts/${slug}`).then((r) => r.data);
export const adminCityEditorialUpdateDraft = (slug, payload) =>
  api.put(`/admin/city-editorial/drafts/${slug}`, { payload }).then((r) => r.data);
export const adminCityEditorialApproveDraft = (slug) =>
  api.post(`/admin/city-editorial/drafts/${slug}/approve`).then((r) => r.data);
export const adminCityEditorialRejectDraft = (slug) =>
  api.post(`/admin/city-editorial/drafts/${slug}/reject`).then((r) => r.data);
export const adminCityEditorialExport = () =>
  api.get("/admin/city-editorial/export").then((r) => r.data);
// Iter-196.FF — bulk batch generation
export const adminCityEditorialBatchGenerate = (payload) =>
  api.post("/admin/city-editorial/batch-generate", payload).then((r) => r.data);
export const adminCityEditorialBatchStatus = () =>
  api.get("/admin/city-editorial/batch-status").then((r) => r.data);

/**
 * Phase 5 Soak-Window Guardian — fetches drift diagnostics between
 * the legacy `lender_rates` collection and the 4 vertical-specific
 * collections. Used by the admin banner to flag dual-write misses
 * the moment they happen.
 */
export const adminPhase5ParityCheck = () =>
  api.get("/admin/phase5-parity-check").then((r) => r.data);

// Iter-50: Bulk article actions + sign-out-everywhere
export const adminArticlesBulkAction = (slugs, action) =>
  api.post("/admin/articles/bulk-action", { slugs, action }).then((r) => r.data);
export const authLogoutEverywhere = () =>
  api.post("/auth/logout-everywhere").then((r) => r.data);

// Iter-54: Article revision history + diff viewer
export const adminListArticleRevisions = (slug) =>
  api.get(`/admin/articles/${slug}/revisions`).then((r) => r.data);
export const adminGetArticleRevision = (slug, revisionId) =>
  api.get(`/admin/articles/${slug}/revisions/${revisionId}`).then((r) => r.data);

// Partner Routing (iter-49: MongoDB-backed registry, replaces in-code literal)
export const fetchPartnerRouting = () =>
  api.get("/partner-routing").then((r) => asArray(r.data?.items));
export const adminListPartnerRouting = () =>
  api.get("/admin/partner-routing").then((r) => r.data);
export const adminUpdatePartnerRouting = (category, payload) =>
  api.put(`/admin/partner-routing/${category}`, payload).then((r) => r.data);

// Iter-165 — Funnel Copy registry (admin-editable per-category copy)
export const fetchFunnelCopy = () =>
  api.get("/funnel-copy").then((r) => asArray(r.data?.items));
export const adminListFunnelCopy = () =>
  api.get("/admin/funnel-copy").then((r) => r.data);
export const adminUpdateFunnelCopy = (category, payload) =>
  api.put(`/admin/funnel-copy/${category}`, payload).then((r) => r.data);
// Iter-52: routing audit-log + webhook event log
export const adminListPartnerRoutingLog = (params = {}) =>
  api
    .get("/admin/partner-routing/log", { params })
    .then((r) => r.data);
export const adminListWebhookEvents = (params = {}) =>
  api
    .get("/admin/webhooks/events", { params })
    .then((r) => r.data);

// Rate alerts
export const createRateAlert = (payload) =>
  api.post("/rate-alerts", payload).then((r) => r.data);
export const deleteRateAlert = (alertId) =>
  api.delete(`/rate-alerts/${alertId}`).then((r) => r.data);
export const adminListRateAlerts = () =>
  api.get("/admin/rate-alerts").then((r) => r.data);
export const adminScanRateAlerts = () =>
  api.post("/admin/rate-alerts/scan").then((r) => r.data);
export const adminSendTestEmail = (payload) =>
  api.post("/admin/email/test-send", payload).then((r) => r.data);

// Iter-188 Sprint C — Email Preference Center.
export const fetchEmailPreferences = (token) =>
  api.get(`/email-preferences/${encodeURIComponent(token)}`).then((r) => r.data);
export const updateEmailPreferences = (token, updates) =>
  api
    .put(`/email-preferences/${encodeURIComponent(token)}`, { updates })
    .then((r) => r.data);
export const requestPreferenceLink = (email) =>
  api.post("/email-preferences/request-link", { email }).then((r) => r.data);

// Iter-189 Sprint D — Abandoned Quote Recovery.
export const recordQuoteAbandoned = (payload) =>
  api.post("/quote-abandoned", payload).then((r) => r.data);

// Iter-190 Sprint E — Admin polish.
export const adminBroadcastMetrics = (broadcastId) =>
  api
    .get(`/admin/newsletter/broadcasts/${encodeURIComponent(broadcastId)}/metrics`)
    .then((r) => r.data);
export const adminEmailDomainHealth = () =>
  api.get("/admin/email/domain-health").then((r) => r.data);

// Iter-186 — Deliverability sprint.
export const adminDeliverabilitySummary = (days = 30) =>
  api
    .get("/admin/deliverability/summary", { params: { days } })
    .then((r) => r.data);
export const adminDeliverabilitySubscriber = (email, limit = 50) =>
  api
    .get("/admin/deliverability/subscriber", { params: { email, limit } })
    .then((r) => r.data);
export const adminDeliverabilitySuppressions = (params = {}) =>
  api
    .get("/admin/deliverability/suppressions", { params })
    .then((r) => r.data);
export const adminDeliverabilityUnsuppress = (email) =>
  api
    .post("/admin/deliverability/suppressions/remove", { email })
    .then((r) => r.data);
export const adminResendWelcome = (to) =>
  api.post("/admin/newsletter/resend-welcome", { to }).then((r) => r.data);
export const adminSendPartnershipOutreach = (payload) =>
  api.post("/admin/partnerships/outreach", payload).then((r) => r.data);
export const adminListPartnershipOutreach = (limit = 200) =>
  api.get(`/admin/partnerships/outreach?limit=${limit}`).then((r) => r.data);
export const adminUpdatePartnershipStatus = (outreach_id, status, note = "") =>
  api
    .patch(`/admin/partnerships/outreach/${outreach_id}`, { status, note })
    .then((r) => r.data);

// SEO / IndexNow
export const adminIndexNowInfo = () =>
  api.get("/admin/seo/indexnow").then((r) => r.data);
export const adminIndexNowSubmitAll = () =>
  api.post("/admin/seo/indexnow/submit-all").then((r) => r.data);

// Iter-191.D — Email Sprint 3 lifecycle sweeps (re-engagement / sunset /
// stale-loan refi trigger). Surfaces the per-flow last-action + 30-day
// totals + a manual "run all sweeps now" trigger.
export const adminEmailLifecycleStatus = () =>
  api.get("/admin/email-lifecycle/status").then((r) => r.data);
export const adminEmailLifecycleRunNow = () =>
  api.post("/admin/email-lifecycle/run-now").then((r) => r.data);

// Iter-83 — Experiments registry (admin CRUD).
export const adminListExperiments = () =>
  api.get("/admin/experiments").then((r) => r.data);
export const adminCreateExperiment = (payload) =>
  api.post("/admin/experiments", payload).then((r) => r.data);
export const adminUpdateExperiment = (key, payload) =>
  api.put(`/admin/experiments/${key}`, payload).then((r) => r.data);
export const adminDeleteExperiment = (key) =>
  api.delete(`/admin/experiments/${key}`).then((r) => r.data);

// Iter-275.BA (Phase 5 #21) — Admin Experiments Results endpoint.
// Returns view + convert counts per variant, sourced from the
// db.experiment_events beacon table populated by the frontend
// `trackExperimentView` / `trackExperimentConvert` helpers.
export const adminExperimentResults = (key) =>
  api.get(`/admin/experiments/${key}/results`).then((r) => r.data);

// Iter-85 — Editorial data sources registry (admin CRUD).
export const adminListDataSources = () =>
  api.get("/admin/data-sources").then((r) => r.data);
// Iter-185.R — Public read endpoint so the speedometer + other UI
// surfaces can pull citation-backed numbers (insurance savings, refi
// savings, quote time, etc.) without bundling them.
export const fetchDataSources = () =>
  api.get("/data-sources").then((r) => r.data);
export const adminCreateDataSource = (payload) =>
  api.post("/admin/data-sources", payload).then((r) => r.data);
export const adminUpdateDataSource = (key, payload) =>
  api.put(`/admin/data-sources/${key}`, payload).then((r) => r.data);
export const adminDeleteDataSource = (key) =>
  api.delete(`/admin/data-sources/${key}`).then((r) => r.data);

// Iter-185.L — Provider Logo CMS.
// Public manifest is keyed by slug so CarrierLogo can do O(1) lookups
// (status:active rows only). Admin endpoints expose the full CRUD +
// multipart logo upload.
export const fetchProviders = (category) =>
  api
    .get("/providers", { params: category ? { category } : {} })
    .then((r) => r.data);
export const adminListProviders = () =>
  api.get("/admin/providers").then((r) => r.data);
// Iter-185.BK — Affiliate revenue dashboard. Pulls clicks → conversions
// → revenue funnel + per-partner breakdown + reconciliation drift.
export const adminFetchRevenueSummary = (windowDays = 30) =>
  api
    .get("/admin/revenue/summary", { params: { window_days: windowDays } })
    .then((r) => r.data);
export const adminFetchEpcByVertical = (windowDays = 90) =>
  api.get("/admin/revenue/epc-by-vertical", { params: { window_days: windowDays } }).then((r) => r.data);
export const adminSavePayoutAssumption = (vertical, body) =>
  api.put(`/admin/revenue/payout-assumptions/${vertical}`, body).then((r) => r.data);
export const adminFetchRevenueReconciliation = (windowDays = 90, driftThresholdPct = 5.0) =>
  api
    .get("/admin/revenue/reconciliation", {
      params: { window_days: windowDays, drift_threshold_pct: driftThresholdPct },
    })
    .then((r) => r.data);
// Iter-193 — Bankrate/NerdWallet-style content scorecard. Surfaces top
// earning pages + per-editor performance + WoW decay refresh queue.
export const adminFetchContentScorecard = (windowDays = 30, limit = 50) =>
  api
    .get("/admin/analytics/content-scorecard", {
      params: { window_days: windowDays, limit },
    })
    .then((r) => r.data);
export const adminFetchEditorPerformance = (windowDays = 30) =>
  api
    .get("/admin/analytics/editor-performance", {
      params: { window_days: windowDays },
    })
    .then((r) => r.data);
// Iter-185.BM (AUDIT #2) — Field-level drop-off dashboard. Aggregates
// `funnel_field_events` per surface so the operator can spot the
// leakiest field per category.
export const adminFetchFunnelDropOff = (params = {}) =>
  api
    .get("/admin/funnel/drop-off", {
      params: {
        window_days: params.windowDays || 30,
        surface: params.surface || undefined,
        category: params.category || undefined,
      },
    })
    .then((r) => r.data);
// Iter-275.AS (Phase 2 #2 of Production Readiness Audit) — Stage-level
// revenue funnel diagnostic. Cross-joins `db.quote_requests` (lead
// capture) and `db.clicks` (partner-routing) to walk the 5-stage
// revenue funnel + surface the biggest leak so the operator can fix
// the 12.7:1 quote→partner-click ratio the audit flagged as P0.
export const adminFetchFunnelStages = (params = {}) =>
  api
    .get("/admin/funnel/stages", {
      params: { window_days: params.windowDays || 7 },
    })
    .then((r) => r.data);
// Iter-185.BE — Provider Logo System Phase 2 owner checklist.
// Returns providers missing logo_url + grouped by-category counts.
export const adminListMissingProviderLogos = () =>
  api.get("/admin/providers/missing-logos").then((r) => r.data);
export const adminCreateProvider = (payload) =>
  api.post("/admin/providers", payload).then((r) => r.data);
export const adminUpdateProvider = (slug, payload) =>
  api.put(`/admin/providers/${slug}`, payload).then((r) => r.data);
export const adminDeleteProvider = (slug) =>
  api.delete(`/admin/providers/${slug}`).then((r) => r.data);
export const adminUploadProviderLogo = (slug, file) => {
  // Multipart upload — let the browser set the boundary automatically
  // by overriding the default JSON content-type.
  const fd = new FormData();
  fd.append("file", file);
  return api
    .post(`/admin/providers/${slug}/upload-logo`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((r) => r.data);
};

// Iter-185.BP — Reset a provider's logo back to the initials-chip
// fallback. Clears `logo_url` in the DB without touching anything
// else on the row. Used when an upload went wrong or a stale
// placeholder is stuck.
export const adminResetProviderLogo = (slug) =>
  api.post(`/admin/providers/${slug}/reset-logo`).then((r) => r.data);

// Iter-185.BT — Editorial Data CMS. Lets the admin update annually-
// refreshing data (J.D. Power scores, NAIC ratios, carrier rate
// filings, state premiums, vehicle + persona multipliers) WITHOUT
// a code redeploy. Public endpoint returns merged defaults+overrides;
// admin endpoints handle the upsert/delete/freshness workflow.
export const fetchEditorialData = () =>
  api.get("/editorial-data").then((r) => r.data);
export const adminListEditorialOverrides = () =>
  api.get("/admin/editorial-overrides").then((r) => r.data);
export const adminUpsertEditorialOverride = (payload) =>
  api.post("/admin/editorial-overrides", payload).then((r) => r.data);
export const adminDeleteEditorialOverride = (scope, slug, field) =>
  api.delete(`/admin/editorial-overrides/${scope}/${slug}/${field}`).then((r) => r.data);
export const adminFetchEditorialFreshness = () =>
  api.get("/admin/editorial-freshness").then((r) => r.data);
export const adminSendEditorialDigest = () =>
  api.post("/admin/editorial-freshness/send-digest").then((r) => r.data);

// Iter-422 — Day-30 SEO change scorecard (baseline vs latest Bing sync).
export const adminFetchSeoScorecard = (label) =>
  api
    .get("/admin/seo/scorecard", { params: label ? { label } : undefined })
    .then((r) => r.data);

// Iter-186.C — Quote triage status tagging.
export const adminSetQuoteStatus = (quoteId, payload) =>
  api.patch(`/admin/quotes/${quoteId}/status`, payload).then((r) => r.data);

// Iter-186.F — State editorial copy (Markdown attached per
// (vertical, state) pair).
export const adminUpsertStateEditorial = (vertical, code, payload) =>
  api.put(`/admin/state-data/${vertical}/${code}/editorial`, payload).then((r) => r.data);

// Iter-187 Sprint 2 — Glossary CMS.
export const fetchGlossary = () =>
  api.get("/glossary").then((r) => r.data);
export const adminListGlossary = () =>
  api.get("/admin/glossary").then((r) => r.data);
export const adminCreateGlossaryTerm = (payload) =>
  api.post("/admin/glossary", payload).then((r) => r.data);
export const adminUpdateGlossaryTerm = (slug, payload) =>
  api.put(`/admin/glossary/${slug}`, payload).then((r) => r.data);
export const adminDeleteGlossaryTerm = (slug) =>
  api.delete(`/admin/glossary/${slug}`).then((r) => r.data);
export const adminDisableGlossaryTerm = (slug, disabled) =>
  api.post(`/admin/glossary/${slug}/disable?disabled=${disabled ? "true" : "false"}`).then((r) => r.data);

// Iter-188 Sprint 3 — Unified Affiliate Control Center.
export const adminFetchAffiliateCenter = () =>
  api.get("/admin/affiliate-center").then((r) => r.data);
export const adminUpdateAffiliateTarget = (kind, slug, payload) =>
  api.patch(`/admin/affiliate-center/${kind}/${slug}`, payload).then((r) => r.data);
export const adminTestAffiliateUrl = (kind, slug) =>
  api.post(`/admin/affiliate-center/${kind}/${slug}/test`).then((r) => r.data);

// Iter-189 Sprint 4 — Homepage Control Center.
export const fetchHomepage = () =>
  api.get("/homepage").then((r) => r.data);
export const adminGetHomepage = () =>
  api.get("/admin/homepage").then((r) => r.data);
export const adminUpsertHomepageSection = (sectionId, fields) =>
  api.put(`/admin/homepage/${sectionId}`, { fields }).then((r) => r.data);
export const adminResetHomepageSection = (sectionId) =>
  api.delete(`/admin/homepage/${sectionId}`).then((r) => r.data);

// Iter-189.5 Sprint 4.5 — Design Health admin dashboard.
export const adminGetDesignHealth = () =>
  api.get("/admin/design-health").then((r) => r.data);

// Iter-189.E Sprint 5 — Footer Control Center.
export const fetchFooter = () =>
  api.get("/footer").then((r) => r.data);
export const adminGetFooter = () =>
  api.get("/admin/footer").then((r) => r.data);
export const adminUpsertFooterSection = (sectionId, fields) =>
  api.put(`/admin/footer/${sectionId}`, { fields }).then((r) => r.data);
export const adminResetFooterSection = (sectionId) =>
  api.delete(`/admin/footer/${sectionId}`).then((r) => r.data);

// Iter-190 Sprint 6 — SEO Power Tools.
//   Phase A — Redirects Manager
export const fetchRedirects = () =>
  api.get("/redirects").then((r) => r.data);
export const logRedirectHit = (sourcePath) =>
  api.post("/redirects/log", { source_path: sourcePath }).catch(() => {});
export const adminListRedirects = () =>
  api.get("/admin/redirects").then((r) => r.data);
export const adminCreateRedirect = (payload) =>
  api.post("/admin/redirects", payload).then((r) => r.data);
export const adminUpdateRedirect = (sourcePath, payload) =>
  api.put(`/admin/redirects/${encodeURIComponent(sourcePath)}`, payload).then((r) => r.data);
export const adminDeleteRedirect = (sourcePath) =>
  api.delete(`/admin/redirects/${encodeURIComponent(sourcePath)}`).then((r) => r.data);

//   Phase B — SEO Health Score (per-page scanner — distinct from
//   legacy /admin/seo-health which does sitemap/DB smoke checks)
export const adminGetSeoHealth = () =>
  api.get("/admin/seo-health/per-page").then((r) => r.data);
export const adminGetSeoLengths = (fresh = false) =>
  api.get("/admin/seo-health/lengths", { params: { fresh } }).then((r) => r.data);
export const adminGetSeoLivePages = () =>
  api.get("/admin/seo-health/live-pages").then((r) => r.data);

//   Phase C — Google Search Console
export const adminGetGscStatus = () =>
  api.get("/admin/gsc/status").then((r) => r.data);
export const adminGetGscTest = () =>
  api.get("/admin/gsc/test").then((r) => r.data);
export const adminGetGscSearchAnalytics = (params = {}) =>
  api.get("/admin/gsc/search-analytics", { params }).then((r) => r.data);
export const adminGetGscCannibalization = (params = {}) =>
  api.get("/admin/gsc/cannibalization", { params }).then((r) => r.data);
export const adminGetGscStrikingDistance = (params = {}) =>
  api.get("/admin/gsc/striking-distance", { params }).then((r) => r.data);

// Click tracking (fire-and-forget; never blocks the user navigation)
export const recordClick = (payload) => {
  try {
    api.post("/clicks", payload).catch(() => {});
  } catch {
    /* swallow */
  }
};


// ─── Iter-278.A — OG Image Upload & Link Preview Manager (admin) ───
export const adminListOgImages = () =>
  api.get("/admin/og-images").then((r) => r.data);

export const adminUploadOgImage = (file, { altText = "", label = "" } = {}) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("alt_text", altText);
  fd.append("label", label);
  return api
    .post("/admin/og-images/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((r) => r.data);
};

export const adminUpdateOgImage = (imageId, patch) =>
  api.patch(`/admin/og-images/${imageId}`, patch).then((r) => r.data);

export const adminDeleteOgImage = (imageId) =>
  api.delete(`/admin/og-images/${imageId}`).then((r) => r.data);

// Iter-278.B — Gemini 3 Flash alt-text suggestion for an uploaded OG image.
export const adminSuggestOgImageAlt = (imageId) =>
  api.post(`/admin/og-images/${imageId}/suggest-alt`).then((r) => r.data);

export const adminListOgImageAssignments = () =>
  api.get("/admin/og-images/assignments").then((r) => r.data);

export const adminSetOgImageAssignment = ({ scope, key = null, imageId }) =>
  api
    .post("/admin/og-images/assignments", { scope, key, image_id: imageId })
    .then((r) => r.data);

export const adminDeleteOgImageAssignment = (assignmentId) =>
  api.delete(`/admin/og-images/assignments/${assignmentId}`).then((r) => r.data);

export const ogImageFileUrl = (imageId) =>
  `${API}/og-images/file/${imageId}`;

// ─── Iter-278.F — AI Quick Answer for FAQ sections ───────────────
// Public endpoint, cached server-side by (page_path, content_hash).
// Frontend hides the widget silently on any error.
export const fetchFaqQuickAnswer = ({ pagePath, items }) =>
  api
    .post("/faq/quick-answer", {
      page_path: pagePath || "/",
      items: (items || []).map((i) => ({
        q: i.q || i.question || "",
        a: i.a || i.answer || "",
      })),
    })
    .then((r) => r.data);
